// ─────────────────────────────────────────────────────────────────────────────
// DeltaRunControl.tsx — THE RUN BUTTON. Buck's shape, with a terminal.
//
// Select a company, press RUN, Delta researches and writes the script, then
// STOPS. The run spans two tabs and ends at the script; nothing here dials,
// sends or books.
//
// Every ruling from `runControl.ts` holds and is enforced in `api/deltaRunner`:
// OFF is always sendable, ON is not · never invent ON · `sending` is a transport
// fact, never a claim about the agent · a failed write RE-READS and never keeps
// the value clicked · a few words, and no epistemology on the screen.
//
// THE ONE ADDITION IS `COMPLETE`, and it exists because Buck's walk never ends
// and Delta's run does. Without it a finished script and a dead runner are the
// same silence.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import {
  DeltaRunnerState, RUNNER_UNREAD, canRun, readRunner, sendRunner,
} from '../../api/deltaRunner';
import RunControlBar from '../agents/RunControlBar';
import { deltaSurface } from '../../lib/runSurface';
import { Selection, getSelection, select, setSelection, subscribeSelection } from '../../lib/deltaSelection';
import {
  DeltaTabKey, DeltaTabs, TABS_UNREAD, readCompanies,
} from '../../api/deltaCompanies';
import { MC } from '../missioncontrol/theme';
import {
  NEEDS_RUN_UNREAD, NeedsRunState, RunUnscriptedAnswer, readNeedsRun, runUnscripted, unscripted,
} from '../../api/deltaNeedsRun';
import NeedsRunPicker from './NeedsRunPicker';

/** How often a RUNNING control asks the producer again. A transport cadence. */
export const RUNNING_REREAD_MS = 2000;
// The confirm press lapses after this long: a STOP pressed once and forgotten must
// not sit armed to end the loop on some later, unrelated click.
export const STOP_CONFIRM_MS = 8000;

export interface DeltaRunControlProps {
  initial?: DeltaRunnerState;
  /** WHICH TAB'S LIST THE SWITCHER OFFERS. The run control is on every Sales
   *  tab, and each tab can be opened only on the companies whose work for THAT
   *  tab exists — research on a Research State record, calls on a stored script.
   *  Omitted, the switcher is not rendered: a tab that names no list gets no
   *  dropdown rather than somebody else's. */
  tab?: DeltaTabKey;
  /** Injected in tests; live, the switcher reads the route. */
  tabs?: DeltaTabs;
  /** Injected in tests. Live, the control reads and writes the route. */
  read?: () => Promise<DeltaRunnerState>;
  send?: (a: 'run' | 'stop', companyId: string | null) => Promise<DeltaRunnerState>;
  /** WHO NEEDS A RUN (owner ruling 2026-09-28). Injected in tests; live, read. */
  needsRun?: NeedsRunState;
  readNeeds?: () => Promise<NeedsRunState>;
  sendBatch?: (companyIds: string[]) => Promise<RunUnscriptedAnswer>;
}

export default function DeltaRunControl(props: DeltaRunControlProps) {
  const [state, setState] = React.useState<DeltaRunnerState>(props.initial || RUNNER_UNREAD);
  const [sending, setSending] = React.useState(false);
  // THE SECOND PRESS (owner ruling 2026-09-17): "Twenty hours is worth one extra
  // press." Held here, never on the producer, and it lapses on its own.
  const [confirming, setConfirming] = React.useState(false);
  const loopHolds = !!state.loop && state.loop.armed;
  React.useEffect(() => {
    if (!confirming) return;
    if (!loopHolds) { setConfirming(false); return; }
    const t = setTimeout(() => setConfirming(false), STOP_CONFIRM_MS);
    return () => clearTimeout(t);
  }, [confirming, loopHolds]);
  const [selection, setSel] = React.useState<Selection>(getSelection());
  const doRead = props.read || readRunner;

  React.useEffect(() => subscribeSelection(setSel), []);
  React.useEffect(() => {
    if (props.initial) return;
    let alive = true;
    doRead().then((s) => { if (alive) setState(s); });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initial]);

  // WHILE RUNNING, IT RE-READS. The route answers a RUN with the state at that
  // instant — `running` — and a control that read once would hold RUNNING over a
  // run that has already stopped. It asks the producer again until the word is
  // no longer `running`; the interval is a transport cadence, not a claim.
  React.useEffect(() => {
    // AND WHILE THE LOOP HOLDS THE BOX — between companies the runner is idle,
    // and the banner must still move with the loop.
    const loopActive = !!state.loop && (state.loop.armed || state.loop.finishing);
    if (props.initial || (state.state !== 'running' && !loopActive)) return;
    let alive = true;
    const t = setInterval(() => {
      doRead().then((s) => { if (alive) setState(s); });
    }, RUNNING_REREAD_MS);
    return () => { alive = false; clearInterval(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.state, state.loop?.armed, state.loop?.finishing, props.initial]);

  // ── THE SWITCHER ────────────────────────────────────────────────────────
  // THE LIST IS WHAT HAS BEEN DONE, NOT WHAT COULD BE DONE. `CompanySelector`
  // answers the other question — Buck's qualified handoff — and lives on the
  // workbook tab. This is how a company that has been WORKED is reopened, and
  // it sets the same selection every Sales tab already reads.
  const [tabs, setTabs] = React.useState<DeltaTabs>(props.tabs || TABS_UNREAD);
  React.useEffect(() => {
    if (props.tabs || !props.tab) return;
    let alive = true;
    readCompanies().then((t) => { if (alive) setTabs(t); });
    return () => { alive = false; };
  }, [props.tabs, props.tab]);
  const list = props.tab ? (props.tabs || tabs).tabs[props.tab] : null;

  const companyId = selection.phase === 'selected' ? selection.companyId : null;

  // ── WHO NEEDS A RUN, AND TODAY'S TRUTH ──────────────────────────────────
  // Re-read whenever the runner's word changes — a run that ends moves a company
  // off the list and a number on the strip — and after the batch press.
  const [needs, setNeeds] = React.useState<NeedsRunState>(props.needsRun || NEEDS_RUN_UNREAD);
  const [answer, setAnswer] = React.useState<RunUnscriptedAnswer | null>(null);
  const doReadNeeds = props.readNeeds || readNeedsRun;
  React.useEffect(() => {
    if (props.needsRun) return;
    let alive = true;
    doReadNeeds().then((n) => { if (alive) setNeeds(n); });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.needsRun, state.state, state.companyId]);
  const loopBusy = !!state.loop && (state.loop.armed || state.loop.finishing);
  // THE SAME STATES RUN IS OFFERED IN — `canRun`'s rule, not a copy of it.
  const canBatch = !sending && !loopBusy && canRun(state.state, true);
  const pressBatch = async () => {
    const ids = unscripted(needs).map((c) => c.companyId);
    if (!ids.length) return;
    setSending(true);
    try {
      const got = await (props.sendBatch || runUnscripted)(ids);
      setAnswer(got);
      // THE ROUTE'S STATE, RE-READ — never the value that was clicked.
      setState(await doRead());
      setNeeds(await doReadNeeds());
    } finally {
      setSending(false);
    }
  };

  const press = async (action: 'run' | 'stop') => {
    setSending(true);
    try {
      const next = props.send
        ? await props.send(action, companyId)
        : await sendRunner({ action, companyId });
      // A FAILED WRITE RE-READS. `next` is what the route answered with — never
      // the value that was clicked, and never an optimistic paint.
      setState(next);
    } finally {
      setSending(false);
    }
  };

  // THE SHARED RUN CONTROL (owner ruling 2026-09-14): one component for Buck and
  // Delta. This adapter owns Delta's read, poll, selection and send; the bar
  // renders the surface and nothing else.
  const surface = deltaSurface({ runner: state, hasCompany: !!companyId, sending, confirmingStop: confirming,
                                 today: needs.today });
  const onStop = () => {
    if (loopHolds && !confirming) { setConfirming(true); return; }
    setConfirming(false);
    press('stop');
  };

  // A TAB WITH NOTHING STILL RENDERS ITS DROPDOWN, DISABLED, WITH THE REASON.
  // An absent control and an empty one are different facts: absent reads as
  // "this tab has no companies", and the truth may be "nothing produces them
  // yet". The producer's sentence is shown; none is composed here.
  const switcher = list ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 10px 0' }}>
      <label htmlFor="delta-switcher"
             style={{ color: MC.dim, fontSize: 11, letterSpacing: '.06em' }}>COMPANY</label>
      <select
        id="delta-switcher"
        aria-label="company"
        value={companyId || ''}
        disabled={list.companies.length === 0}
        onChange={(e) => setSelection(e.target.value ? select(e.target.value) : getSelection())}
        style={{
          background: MC.bg2, color: MC.text, border: `1px solid ${MC.line2}`,
          borderRadius: 10, padding: '5px 10px', font: 'inherit', fontSize: 13, minWidth: 260,
        }}
      >
        <option value="">— select —</option>
        {list.companies.map((c) => (
          <option key={c.companyId} value={c.companyId}>
            {c.business}{c.fit ? ' · ' + c.fit : ''}{c.researchState ? ' · ' + c.researchState : ''}
          </option>
        ))}
      </select>
      {list.companies.length === 0 && (
        <span style={{ color: MC.muted, fontSize: 12 }}>{list.reason || 'NONE YET'}</span>
      )}
    </div>
  ) : null;

  return (
    <div>
      <NeedsRunPicker
        state={needs}
        selectedId={companyId}
        onSelect={(id) => setSelection(id ? select(id) : getSelection())}
        onRunUnscripted={pressBatch}
        canBatch={canBatch}
        answer={answer}
      />
      {switcher}
      <RunControlBar
      surface={surface}
      onRun={() => press('run')}
      onStop={onStop}
      sending={sending}
      note={companyId ? null : 'SELECT A COMPANY'}
      testId="delta-run-control"
    />
    </div>
  );
}
